import { motion, useAnimationControls } from 'motion/react';

import { Icon } from '~/modules/elements/akui/icon';
import { PlayerNumber } from '~/modules/players/player-number';
import { playerColourAlpha, playerColourName, playerInk } from '~/routes/remote-mic/components/player-colour';

interface Props {
  playerNumbers: PlayerNumber[];
  selected: PlayerNumber | null;
  // What this phone's own tile reads once it holds it.
  ownName: string;
  occupants: Partial<Record<PlayerNumber, string>>;
  onPick: (playerNumber: PlayerNumber) => void;
  onBlocked: (playerNumber: PlayerNumber, occupant: string) => void;
  // Joining locks a colour somebody holds; moving a phone from Manage the game may take it off them.
  allowOccupied?: boolean;
}

export default function ColourTiles({
  playerNumbers,
  selected,
  ownName,
  occupants,
  onPick,
  onBlocked,
  allowOccupied = false,
}: Props) {
  // Eight colours stay two across, so a singer's name still fits a tile, but each one is half as tall.
  const compact = playerNumbers.length > 4;

  return (
    <div className={`grid grid-cols-2 ${compact ? 'gap-2' : 'gap-2.5'}`}>
      {playerNumbers.map((number) => (
        <ColourTile
          key={number}
          compact={compact}
          number={number}
          occupant={number === selected ? undefined : occupants[number]}
          isOwn={number === selected}
          ownName={ownName}
          onPick={onPick}
          onBlocked={onBlocked}
          allowOccupied={allowOccupied}
        />
      ))}
    </div>
  );
}

function ColourTile({
  number,
  occupant,
  isOwn,
  ownName,
  onPick,
  onBlocked,
  allowOccupied,
  compact,
}: {
  compact: boolean;
  number: PlayerNumber;
  occupant?: string;
  isOwn: boolean;
  ownName: string;
  onPick: Props['onPick'];
  onBlocked: Props['onBlocked'];
  allowOccupied: boolean;
}) {
  const nudge = useAnimationControls();
  const occupied = !!occupant;
  const taken = occupied && !allowOccupied;

  const press = () => {
    if (taken) {
      void nudge.start({ x: [0, -5, 5, 0], transition: { duration: 0.34 } });
      onBlocked(number, occupant);
      return;
    }
    onPick(number);
  };

  return (
    <motion.button
      type="button"
      animate={nudge}
      onClick={press}
      data-test={`colour-slot-${number}`}
      data-taken={taken}
      data-selected={isOwn}
      // Shorter on a short screen, so the join step's own button stays on it.
      className={`flex cursor-pointer flex-col justify-between border-none text-left leading-tight transition-transform duration-100 active:scale-[0.97] ${
        compact ? 'h-[min(4.5rem,9dvh)] rounded-[1rem] px-3.5 py-2.5' : 'h-[min(9.375rem,17dvh)] rounded-[1.375rem] p-4'
      }`}
      style={{
        background: playerColourAlpha(number, occupied ? 0.28 : 0.9),
        color: occupied ? '#fff' : playerInk(number),
        opacity: occupied ? 0.6 : 1,
        boxShadow: isOwn ? 'inset 0 0 0 4px #fff' : undefined,
      }}>
      <span className="text-[1.125rem] font-bold">{playerColourName(number)}</span>
      <span className="flex min-w-0 items-center gap-1.5 text-[0.9375rem] font-bold">
        <Icon
          icon={
            taken
              ? 'ic:outline-lock'
              : occupied
                ? 'ic:baseline-swap-horiz'
                : isOwn
                  ? 'ic:outline-check-circle'
                  : 'ic:baseline-radio-button-unchecked'
          }
          size={5}
          className="shrink-0"
        />
        {occupied || isOwn ? (
          <span data-test="colour-slot-occupant" className="ph-no-capture min-w-0 truncate">
            {occupied ? occupant : ownName}
          </span>
        ) : (
          <span className="min-w-0 truncate">Free</span>
        )}
      </span>
    </motion.button>
  );
}
